"use client";

import { Check, ChevronDown, FileText, Heart, LayoutGrid } from "lucide-react";
import { useTranslations } from "next-intl";
import type React from "react";
import { useTransition } from "react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { usePathname, useRouter } from "@/i18n/navigation";

type Scope = "all" | "favorites" | "drafts";

const SCOPE_ICONS: Record<Scope, React.ComponentType<{ className?: string }>> = {
  all: LayoutGrid,
  favorites: Heart,
  drafts: FileText,
};

const SCOPES: Scope[] = ["all", "favorites", "drafts"];

interface RecipeScopeDropdownProps {
  selected: Scope;
  query?: string;
  tags: string[];
}

export function RecipeScopeDropdown({ selected, query, tags }: RecipeScopeDropdownProps) {
  const t = useTranslations("recipes");
  const router = useRouter();
  const pathname = usePathname();
  const [, startTransition] = useTransition();

  const navigate = (next: Scope) => {
    if (next === selected) return;
    const params = new URLSearchParams();
    if (query) params.set("q", query);
    if (tags.length > 0) params.set("tags", tags.join(","));
    if (next !== "all") params.set("scope", next);
    const target = params.toString() ? `${pathname}?${params}` : pathname;
    startTransition(() => {
      router.replace(target, { scroll: false });
    });
  };

  const SelectedIcon = SCOPE_ICONS[selected];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <SelectedIcon className="size-4 shrink-0" aria-hidden />
          <span className="truncate">{t(`scope_${selected}` as const)}</span>
          <ChevronDown className="size-4 shrink-0" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-44 mx-4">
        {SCOPES.map((s) => {
          const Icon = SCOPE_ICONS[s];
          return (
            <DropdownMenuItem
              key={s}
              onSelect={() => navigate(s)}
              className="flex items-center gap-2"
            >
              <Icon className="size-4 shrink-0" aria-hidden />
              <span className="flex-1">{t(`scope_${s}` as const)}</span>
              {s === selected && <Check className="size-4 shrink-0" aria-hidden />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
